import $ from 'jquery';
import Cookie from 'js-cookie';
import ConsentForm from "./ConsentForm";

let staticInstance = null;

const SERVICE_SEPARATOR = '.';

class ConsentEvent {
    constructor(data) {
        $.extend(this, data || {});
    }
}


class ConsentApi {
    constructor(opts) {
        /**
         * @type {ConsentApiOptions}
         * @public
         */
        this.opts = $.extend({
            cookieName: 'dsgvo-settings',
            cookieVersion: null,
            cookiePath: '/',
            snippetUrl: null,
            popupUrl: null,
        }, opts || {});

        /** @type {ScheduledEvents} */
        this._scheduledEvents = {};
    }

    static getInstance(...args) {
        if (args.length === 0) {
            args = [window.consentApiOptions];
        }
        if (staticInstance === null) {
            staticInstance = new ConsentApi(...args);
        }
        return staticInstance;
    }

    /**
     * @param {String} eventString - "enable:youtube" or "disable:google-maps"
     * @param {CategoryChangeCallback} callback
     * @param {*} [payload]
     */
    on(eventString, callback, payload) {
        eventString = eventString.split(':').map(s => s.trim()).join(':');
        this._scheduleEvent(eventString, {callback, payload});
    }

    /**
     * @param {String} eventString - one of "enable" or "disable"
     * @param {ScheduledEvent} event
     * @private
     */
    _scheduleEvent(eventString, event) {
        if (!this._scheduledEvents.hasOwnProperty(eventString)) {
            this._scheduledEvents[eventString] = [];
        }
        this._scheduledEvents[eventString].push(event);

        let {eventPart, identifierPart} = this._splitEvent(eventString);
        if (eventPart === 'enable' && this.isEnabled(identifierPart)) {
            event.callback(this._makeEvent(eventString), event.payload);
        } else if (eventPart === 'disable' && this.isDisabled(identifierPart)) {
            event.callback(this._makeEvent(eventString), event.payload);
        }
    }

    _splitEvent(eventString) {
        let parts = eventString.split(':');
        let eventPart = parts[0];
        let identifierPart = parts.slice(1).join(':');
        return {
            eventPart: eventPart,
            identifierPart: (identifierPart ? identifierPart : null),
        };
    }

    /**
     * @param {String} eventString
     * @param {*} [extra]
     * @private
     */
    _triggerChange(eventString, extra) {
        if (!this._scheduledEvents.hasOwnProperty(eventString)) {
            return;
        }
        let events = this._scheduledEvents[eventString];
        if (!Array.isArray(events)) {
            return;
        }
        const ev = this._makeEvent(eventString, extra);
        for (let i = 0; i < events.length; i++) {
            events[i].callback(ev, events[i].payload);
        }
    }

    /**
     * @param {String} eventString
     * @param {*} [extra]
     * @return {ConsentEvent}
     * @private
     */
    _makeEvent(eventString, extra) {
        let {eventPart, identifierPart} = this._splitEvent(eventString);
        if (identifierPart) {
            return new ConsentEvent({event: eventPart, identifier: identifierPart});
        } else {
            let eventData = $.extend({}, extra || {}, {event: eventPart});
            return new ConsentEvent(eventData);
        }
    }

    /**
     * @return {CookieSetup}
     */
    settings() {
        let settings = null;
        try {
            let cookieData = Cookie.get(this.opts.cookieName);
            if (cookieData) {
                settings = JSON.parse(cookieData);
            }
        } catch (e) {
            console.warn(e);
        }

        if (!settings || !settings.confirmed || settings.version !== this.opts.cookieVersion) {
            settings = {confirmed: false, identifiers: []};
        }

        if (!Array.isArray(settings.identifiers)) {
            settings.identifiers = [];
        }

        return settings;
    }

    /**
     * @return {String[]}
     */
    identifiers() {
        let settings = this.settings();
        return settings.identifiers.slice();
    }

    /**
     * @param identifier {String} - category identifier or service token ("category.service")
     * @param force {Boolean} - mostly used by inline element, to enforce confirmation
     */
    enable(identifier, force = false) {
        let before = this.settings();
        let settings = this.settings();
        settings.confirmed = settings.confirmed || force;

        if (settings.confirmed && !this._isAccepted(settings.identifiers, identifier)) {
            // an accepted category covers all its services
            settings.identifiers = settings.identifiers.filter(e => !this._isServiceOf(e, identifier));
            settings.identifiers.push(identifier);
            this._save(settings);
            this._triggerChanges(before, settings);
        }
    }

    /**
     * Disabling a single service has no effect while its whole category is accepted.
     *
     * @param identifier {String} - category identifier or service token ("category.service")
     * @param force {Boolean} - mostly used by inline element, to enforce confirmation
     */
    disable(identifier, force=false) {
        let before = this.settings();
        let settings = this.settings();
        settings.confirmed = settings.confirmed || force;

        let identifiers = settings.identifiers.filter(e => e !== identifier && !this._isServiceOf(e, identifier));
        if (settings.confirmed && identifiers.length !== settings.identifiers.length) {
            settings.identifiers = identifiers;
            this._save(settings);
            this._triggerChanges(before, settings);
        }
    }

    /**
     * @param identifier {String} - category identifier or service token ("category.service")
     * @return {boolean}
     */
    isEnabled(identifier) {
        return this.isConfirmed() && this._isAccepted(this.identifiers(), identifier);
    }

    /**
     * @param {String[]} identifiers
     * @param {String} identifier
     * @return {boolean}
     * @private
     */
    _isAccepted(identifiers, identifier) {
        if (identifiers.indexOf(identifier) !== -1) {
            return true;
        }
        let separatorPosition = identifier.lastIndexOf(SERVICE_SEPARATOR);
        return separatorPosition !== -1 && identifiers.indexOf(identifier.substring(0, separatorPosition)) !== -1;
    }

    /**
     * @param {String} identifier
     * @param {String} categoryIdentifier
     * @return {boolean}
     * @private
     */
    _isServiceOf(identifier, categoryIdentifier) {
        return identifier.startsWith(categoryIdentifier + SERVICE_SEPARATOR);
    }

    /**
     * Also covers services that changed through their category, which never appear in the cookie themselves.
     *
     * @param {CookieSetup} before
     * @param {CookieSetup} after
     * @private
     */
    _triggerChanges(before, after) {
        let identifiers = new Set([...before.identifiers, ...after.identifiers]);
        Object.keys(this._scheduledEvents).forEach(eventString => {
            let {identifierPart} = this._splitEvent(eventString);
            if (identifierPart) {
                identifiers.add(identifierPart);
            }
        });

        identifiers.forEach(identifier => {
            let wasEnabled = before.confirmed && this._isAccepted(before.identifiers, identifier);
            let isEnabled = after.confirmed && this._isAccepted(after.identifiers, identifier);
            if (!wasEnabled && isEnabled) {
                this._triggerChange(`enable:${identifier}`);
            } else if (wasEnabled && !isEnabled) {
                this._triggerChange(`disable:${identifier}`);
            }
        });
    }

    /**
     * @param identifier {String}
     * @return {boolean}
     */
    isDisabled(identifier) {
        return !this.isEnabled(identifier);
    }

    /**
     * @param identifiers {String[]}
     */
    confirm(identifiers) {
        this._triggerChange('confirming', {identifiers});
        let before = this.settings();
        let settings = this.settings();
        settings.confirmed = true;
        settings.identifiers = identifiers.slice();
        this._save(settings);
        this.loadSnippets();
        this._triggerChanges(before, settings);
        this._triggerChange('confirmed', {identifiers});
    }

    /**
     * @return {Boolean}
     */
    isConfirmed() {
        let settings = this.settings();
        return !!(settings && settings.confirmed);
    }

    openPopup() {
        return new Promise(resolve => {
            this.loadPopup().then($popup => {
                $(document.body).append($popup);
                let popupForm = new ConsentForm($popup, window.consentFormOptions, this);
                window.consentForms.push(popupForm);
                resolve(popupForm);
            });
        });
    }

    loadPopup() {
        return new Promise((resolve, reject) => {
            fetch(this.opts.popupUrl).then(e => e.json()).then(/** @param data {ConsentPopupResponse} */data => {
                resolve($(data.popup));
            }).catch(reject);
        });
    }

    loadSnippets() {
        fetch(this.opts.snippetUrl).then(e => e.json()).then(/** @param data {ConsentSnippetResponse} */data => {
            data.snippets.head.forEach(snippet => {
                this._attachSnippet(snippet, document.head, 'end');
            });
            data.snippets.body_start.forEach(snippet => {
                this._attachSnippet(snippet, document.body, 'start');
            });
            data.snippets.body_end.forEach(snippet => {
                this._attachSnippet(snippet, document.body, 'end');
            });
        });
    }

    _attachSnippet(snippet, parent, rel) {
        this._attachedSnippets = this._attachedSnippets || [];
        if (this._attachedSnippets.indexOf(snippet.uid) !== -1) {
            // snippet already loaded and attached.
            return;
        }

        if (rel === 'end') {
            this._attachedSnippets.push(snippet.uid);
            $(parent).append(snippet.markup);
        } else if (rel === 'start') {
            this._attachedSnippets.push(snippet.uid);
            $(parent).prepend(snippet.markup);
        }
    }

    /**
     * @param settings {CookieSetup}
     * @private
     */
    _save(settings) {
        let date;
        if (settings.expires) {
            date = new Date(settings.expires);
        } else {
            date = new Date(Date.now() + (864e5 * 30));
        }
        settings.version = this.opts.cookieVersion;
        let settingsJson = JSON.stringify(settings)
        Cookie.set(this.opts.cookieName, settingsJson, {
            expires: date,
            path: this.opts.cookiePath,
        });
    }
}

export default ConsentApi;
